import { Analytics as VercelAnalytics } from '@vercel/analytics/react'
import Footer from 'components/Footer'
import Loader from 'components/Loader'
import { FilterProvider } from 'contexts/FilterContext'
import { TokenDataProvider } from 'contexts/TokenDataContext'
import useSyncChainQuery from 'hooks/useSyncChainQuery'
import { Suspense, useEffect, useRef } from 'react'
import { Outlet, Route, Routes, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { isMobile } from 'utils/userAgent'

import ErrorBoundary from '../components/ErrorBoundary'
import HeaderKodiak from '../components/HeaderKodiak'
import Popups from '../components/Popups'
import AddLiquidity from './AddLiquidity'
import { RedirectDuplicateTokenIds } from './AddLiquidity/redirects'
import { RedirectDuplicateTokenIdsV2 } from './AddLiquidityV2/redirects'
import Analytics from './Analytics'
import AnalyticsPoolPage from './Analytics/Pool'
import AnalyticsTokenPage from './Analytics/Token'
import ClaimPage from './Claim'
import DividendsPage from './Dividends'
import Farms from './Farms'
import FarmPage from './Farms/FarmPage'
import BaultAnalytics from './InternalAnalytics/BaultAnalytics'
import FarmConfiguration from './KodiakFarmsConfigure'
import KodiakFarmsDeploy from './KodiakFarmsDeploy'
import Liquidity from './Liquidity'
import { GenericFarmRouteHandler, PoolRouteHandler } from './Liquidity/Routes'
import { POOL_ROUTES } from './Liquidity/Routes/config'
import { PositionPage } from './Pool/PositionPage'
import PoolFinder from './PoolFinder'
import Porfolio from './Portfolio'
import RemoveLiquidity from './RemoveLiquidity'
import RemoveLiquidityV3 from './RemoveLiquidity/V3'
import Staking from './Staking'
import Swap from './Swap'
import { BeraOnlyRoute, RedirectPathToSwapOnly, RedirectToSwap } from './Swap/redirects'
const AppWrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  height: 100vh;
  background: ${({ theme }) => theme.backgroundBackdrop};
`

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 120px 16px 0px 16px;
  align-items: center;
  flex: 1;
  z-index: 1;
  background: ${({ theme }) => theme.backgroundBackdrop};

  /* testing a smoother light mode transition */
  * {
    transition: color 0.1s ease-in;
    transition: background-color 0.3s ease-out;
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 4rem 8px 16px 8px;
  `};
`

const HeaderWrapper = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  justify-content: space-between;
  position: fixed;
  top: 0;
  z-index: 2;
`

const Marginer = styled.div`
  margin-top: 5rem;
`

export default function App() {
  const isLoaded = true

  useSyncChainQuery()

  const { pathname } = useLocation()
  const previousPathRef = useRef<string | null>(null)

  useEffect(() => {
    const previousPath = previousPathRef.current
    const isAnalyticsPath = (path?: string | null) => Boolean(path?.startsWith('/explore/'))
    const shouldSkipScroll = Boolean(
      previousPath && isAnalyticsPath(previousPath) && isAnalyticsPath(pathname) && isMobile
    )

    if (!shouldSkipScroll) {
      window.scrollTo(0, 0)
    }

    previousPathRef.current = pathname
  }, [pathname])

  return (
    <ErrorBoundary>
      <TokenDataProvider>
        <FilterProvider>
          <AppWrapper>
            <HeaderWrapper>
              <HeaderKodiak />
            </HeaderWrapper>
            <BodyWrapper>
              <Popups />
              <Suspense fallback={<Loader />}>
                {isLoaded ? (
                  <Routes>
                    <Route
                      path="claim"
                      element={
                        <BeraOnlyRoute>
                          <ClaimPage />
                        </BeraOnlyRoute>
                      }
                    />
                    <Route
                      path="staking"
                      element={
                        <BeraOnlyRoute>
                          <Staking />
                        </BeraOnlyRoute>
                      }
                    />
                    <Route path="internal/bault-analytics" element={<BaultAnalytics />} />
                    <Route
                      path="staking/rewards"
                      element={
                        <BeraOnlyRoute>
                          <DividendsPage />
                        </BeraOnlyRoute>
                      }
                    />

                    <Route path="send" element={<RedirectPathToSwapOnly />} />
                    <Route path="swap/:outputCurrency" element={<RedirectToSwap />} />
                    <Route path="swap" element={<Swap />} />

                    <Route path="liquidity" element={<Liquidity />} />
                    <Route path="liquidity/islands" element={<Liquidity />} />
                    <Route path="liquidity/pools" element={<Liquidity />} />
                    {POOL_ROUTES.map(({ path, component: Component, type }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          type === 'farm' ? (
                            <BeraOnlyRoute>
                              <GenericFarmRouteHandler>{(props) => <Component {...props} />}</GenericFarmRouteHandler>
                            </BeraOnlyRoute>
                          ) : (
                            <PoolRouteHandler>{(props) => <Component {...props} />}</PoolRouteHandler>
                          )
                        }
                      />
                    ))}

                    <Route path="liquidity/v2" element={<Liquidity />} />
                    <Route path="liquidity/v2/find" element={<PoolFinder />} />
                    <Route path="liquidity/v2/remove/:currencyIdA/:currencyIdB" element={<RemoveLiquidity />} />
                    <Route path="liquidity/v2/add" element={<RedirectDuplicateTokenIdsV2 />}>
                      <Route path=":currencyIdA" />
                      <Route path=":currencyIdA/:currencyIdB" />
                    </Route>

                    <Route path="liquidity/v3" element={<Liquidity />} />
                    <Route path="liquidity/v3/:tokenId" element={<PositionPage />} />
                    <Route path="liquidity/v3/remove/:tokenId" element={<RemoveLiquidityV3 />} />

                    <Route path="liquidity/v3/add" element={<RedirectDuplicateTokenIds />}>
                      {/* this is workaround since react-router-dom v6 doesn't support optional parameters any more */}
                      <Route path=":currencyIdA" />
                      <Route path=":currencyIdA/:currencyIdB" />
                      <Route path=":currencyIdA/:currencyIdB/:feeAmount" />
                    </Route>

                    <Route path="liquidity/v3/increase" element={<AddLiquidity />}>
                      <Route path=":currencyIdA" />
                      <Route path=":currencyIdA/:currencyIdB" />
                      <Route path=":currencyIdA/:currencyIdB/:feeAmount" />
                      <Route path=":currencyIdA/:currencyIdB/:feeAmount/:tokenId" />
                    </Route>

                    <Route
                      element={
                        <BeraOnlyRoute>
                          <Outlet />
                        </BeraOnlyRoute>
                      }
                    >
                      <Route path="liquidity/farms" element={<Liquidity />} />
                      <Route path="liquidity/farms/deploy" element={<KodiakFarmsDeploy />} />
                      <Route path="liquidity/farms/configure/:farmId" element={<FarmConfiguration />} />
                      <Route path="farms" element={<Farms />} />
                      <Route path="farms/:farmId" element={<FarmPage />} />
                    </Route>

                    <Route path="explore" element={<Analytics />} />
                    <Route path="explore/:version" element={<Analytics />} />
                    <Route path="explore/:version/:view" element={<Analytics />} />
                    <Route path="explore/:version/pools/:poolId" element={<AnalyticsPoolPage />} />
                    <Route path="explore/:version/tokens/:tokenId" element={<AnalyticsTokenPage />} />

                    <Route path="portfolio" element={<Porfolio />} />

                    <Route path="*" element={<RedirectPathToSwapOnly />} />
                  </Routes>
                ) : (
                  <Loader />
                )}
              </Suspense>
              <Marginer />
            </BodyWrapper>
            <Footer />
            <VercelAnalytics />
          </AppWrapper>
        </FilterProvider>
      </TokenDataProvider>
    </ErrorBoundary>
  )
}
